import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Badge, 
  Icon, 
  SimpleGrid, 
  Divider, 
  Button,
  Flex,
  useColorModeValue,
  Progress,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Collapse,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Tooltip as ChakraTooltip,
  Grid,
  GridItem,
  Circle,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { SectionLabel } from './DashboardWrapper';
import { 
  FaStar, 
  FaUsers, 
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaChartLine,
  FaThumbsUp,
  FaLightbulb,
  FaTrophy,
  FaBalanceScale,
  FaArrowUp,
  FaArrowDown,
  FaChartBar,
  FaCrown,
  FaMedal,
  FaAward,
} from 'react-icons/fa';
import { GiStrongMan } from 'react-icons/gi';
import { MdOutlineSick } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import { logger } from '../../utils/logger';
import { 
  ResponsiveContainer, 
  Tooltip as RechartsTooltip, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';
import { getYearData } from '../../services/yearService';
import { useNavigate } from 'react-router-dom';
import {
  getTrafficValue,
  sortBusinessesBySeoScore,
} from '../../utils/competitiveIntelHelpers';
import { getBusinessImages } from '../../services/imageService';
import { getHistoricalTraffic } from '../../services/reviewService';
import BusinessReviewModal from '../BusinessReviewModal';
import BusinessIntelCard from '../core/BusinessIntelCard';

// Color palette for businesses - distinct, high-contrast colors for clear differentiation
const BUSINESS_COLORS = [
  '#2563EB', // vivid blue
  '#DC2626', // crimson red
  '#059669', // emerald green
  '#EA580C', // bright orange
  '#7C3AED', // rich purple
  '#DB2777', // hot pink
  '#0891B2', // cyan/teal
  '#CA8A04', // golden yellow
  '#475569', // slate gray
  '#65A30D', // lime green
];

// Sentiment colors
const SENTIMENT_COLORS = {
  positive: '#10B981',
  neutral: '#F59E0B',
  negative: '#EF4444',
};

// SWOT section colors
const SWOT_COLORS = {
  strengths: { bg: 'green.50', border: 'green.200', heading: 'green.600', darkBg: 'green.900', darkBorder: 'green.700' },
  weaknesses: { bg: 'red.50', border: 'red.200', heading: 'red.600', darkBg: 'red.900', darkBorder: 'red.700' },
};

/**
 * Parse SWOT sections from versus_insights content
 * @param {string} content - The versus_insights markdown content
 * @returns {Object} - Object with strengths and weaknesses sections
 */
const parseSwotSections = (content) => {
  if (!content) return { strengths: '', weaknesses: '' };

  const sections = {
    strengths: '',
    weaknesses: '',
  };

  // Improved regex patterns that can handle markdown formatting with ** asterisks
  const strengthsRegex = /\*\*\s*strengths:?\s*\*\*/i;
  const weaknessesRegex = /\*\*\s*weaknesses:?\s*\*\*/i;
  const opportunitiesRegex = /\*\*\s*opportunities:?\s*\*\*/i;

  // Try to find section positions
  const strengthsMatch = content.match(strengthsRegex);
  const weaknessesMatch = content.match(weaknessesRegex);
  const opportunitiesMatch = content.match(opportunitiesRegex);

  if (strengthsMatch && weaknessesMatch) {
    const strengthsPos = strengthsMatch.index;
    const weaknessesPos = weaknessesMatch.index;
    const opportunitiesPos = opportunitiesMatch ? opportunitiesMatch.index : content.length;

    sections.strengths = content
      .substring(strengthsPos + strengthsMatch[0].length, weaknessesPos)
      .trim();

    sections.weaknesses = content
      .substring(weaknessesPos + weaknessesMatch[0].length, opportunitiesPos)
      .trim();

    return sections;
  }

  // Try plain text format
  const plainStrengthsRegex = /strengths:?(?:\s*\n|\s*$)/i;
  const plainWeaknessesRegex = /weaknesses:?(?:\s*\n|\s*$)/i;
  const plainOpportunitiesRegex = /opportunities:?(?:\s*\n|\s*$)/i;

  const plainStrengthsMatch = content.match(plainStrengthsRegex);
  const plainWeaknessesMatch = content.match(plainWeaknessesRegex);
  const plainOpportunitiesMatch = content.match(plainOpportunitiesRegex);

  if (plainStrengthsMatch && plainWeaknessesMatch) {
    const strengthsPos = plainStrengthsMatch.index;
    const weaknessesPos = plainWeaknessesMatch.index;
    const opportunitiesPos = plainOpportunitiesMatch ? plainOpportunitiesMatch.index : content.length;

    sections.strengths = content
      .substring(strengthsPos + plainStrengthsMatch[0].length, weaknessesPos)
      .trim();

    sections.weaknesses = content
      .substring(weaknessesPos + plainWeaknessesMatch[0].length, opportunitiesPos)
      .trim();

    return sections;
  }

  // Try line-based parsing
  const lines = content.split('\n');
  let currentSection = null;

  for (const line of lines) {
    const trimmedLine = line.trim();
    const lowerLine = trimmedLine.toLowerCase();

    if (
      trimmedLine.match(/\*\*\s*strengths:?\s*\*\*/i) ||
      lowerLine === '**strengths**' ||
      lowerLine === 'strengths:'
    ) {
      currentSection = 'strengths';
      continue;
    } else if (
      trimmedLine.match(/\*\*\s*weaknesses:?\s*\*\*/i) ||
      lowerLine === '**weaknesses**' ||
      lowerLine === 'weaknesses:'
    ) {
      currentSection = 'weaknesses';
      continue;
    } else if (
      trimmedLine.match(/\*\*\s*opportunities:?\s*\*\*/i) ||
      lowerLine === '**opportunities**' ||
      lowerLine === 'opportunities:'
    ) {
      currentSection = null;
      continue;
    }

    if (currentSection && trimmedLine) {
      sections[currentSection] += line + '\n';
    }
  }

  return {
    strengths: sections.strengths.trim(),
    weaknesses: sections.weaknesses.trim(),
  };
};

// Markdown components for SWOT content rendering
const swotMarkdownComponents = {
  p: (props) => (
    <Text fontSize="sm" color="gray.600" mb={2} lineHeight="1.6" {...props} />
  ),
  strong: (props) => (
    <Text as="span" fontWeight="bold" {...props} />
  ),
  em: (props) => (
    <Text as="span" fontStyle="italic" {...props} />
  ),
  ul: ({ children, ...props }) => (
    <Box as="ul" pl={4} mb={2} {...props}>
      {children}
    </Box>
  ),
  li: ({ children, ...props }) => (
    <Box as="li" mb={1} fontSize="sm" color="gray.600" {...props}>
      {children}
    </Box>
  ),
};

const calculateThreeMonthGrowth = (monthlyReviewCounts = {}, months = []) => {
  if (months.length < 3) return 0;
  
  const lastThreeMonths = months.slice(-3);
  return lastThreeMonths.reduce((sum, month) => sum + (monthlyReviewCounts[month] || 0), 0);
};

const BusinessInsightsDashboard = ({ reviewsData, demoMode, demoData }) => {
  // Move all useColorModeValue hooks to the top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const searchBg = useColorModeValue('white', 'gray.700');
  const searchBorder = useColorModeValue('gray.200', 'gray.600');
  
  // React hooks for state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  // In demo mode all data is available synchronously — initialize immediately to avoid
  // an unnecessary async round-trip and loading spinner.
  const [yearData, setYearData] = useState(() =>
    demoMode && demoData ? (demoData.yearData || []) : []
  );
  const [isLoading, setIsLoading] = useState(() => !(demoMode && demoData));
  const [isCardsExpanded, setIsCardsExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('growth');
  
  // Add a unified loading state to prevent flashing
  const [contentReady, setContentReady] = useState(false);
  
  // State for business images - maps_uri -> image URL
  const [businessImages, setBusinessImages] = useState(() =>
    demoMode && demoData ? (demoData.businessImages || {}) : {}
  );
  
  // State for historical traffic data - maps_uri -> timeSeries array
  const [trafficHistory, setTrafficHistory] = useState(() =>
    demoMode && demoData ? (demoData.trafficHistory || {}) : {}
  );
  
  // State for comparison section
  const [compareSelectedBusinesses, setCompareSelectedBusinesses] = useState([]);
  const [businessColors, setBusinessColors] = useState({});
  const [isCompareExpanded, setIsCompareExpanded] = useState(true);
  
  // Additional color mode values for comparison section
  const strengthsBg = useColorModeValue(SWOT_COLORS.strengths.bg, SWOT_COLORS.strengths.darkBg);
  const strengthsBorder = useColorModeValue(SWOT_COLORS.strengths.border, SWOT_COLORS.strengths.darkBorder);
  const weaknessesBg = useColorModeValue(SWOT_COLORS.weaknesses.bg, SWOT_COLORS.weaknesses.darkBg);
  const weaknessesBorder = useColorModeValue(SWOT_COLORS.weaknesses.border, SWOT_COLORS.weaknesses.darkBorder);
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const accentColor = useColorModeValue('blue.500', 'blue.400');

  const navigate = useNavigate();

  // Fetch year data
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setIsLoading(false);
      return;
    }
    let mounted = true;
    
    const fetchYearData = async () => {
      try {
        const data = await getYearData();
        if (mounted) {
          setYearData(data);
          // Don't set isLoading to false yet - wait for data processing
        }
      } catch (error) {
        console.error('Error fetching year data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchYearData();
    
    return () => {
      mounted = false;
    };
  }, [demoMode, demoData]);

  // Fetch business images (async, non-blocking)
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          // Create a map of maps_uri -> image URL
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        console.error('Error fetching business images:', error);
        // Don't block UI on image fetch failure
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch historical traffic data (async, non-blocking)
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchTrafficData = async () => {
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          // Create a map of maps_uri -> timeSeries data
          const trafficMap = {};
          result.businesses.forEach(business => {
            if (business.mapsUri && business.timeSeries) {
              // Process time series to get chart-ready data (same format as BusinessReviewModal)
              const chartData = business.timeSeries
                .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
                .slice(-12) // Last 12 months
                .map(point => ({
                  month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit'
                  }),
                  organicClicks: point.organicClicks || 0
                }));
              trafficMap[business.mapsUri] = chartData;
            }
          });
          setTrafficHistory(trafficMap);
        }
      } catch (error) {
        console.error('Error fetching traffic history:', error);
        // Don't block UI on traffic fetch failure
      }
    };

    fetchTrafficData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Initialize comparison selection when yearData loads
  useEffect(() => {
    if (yearData && yearData.length > 0 && compareSelectedBusinesses.length === 0) {
      // Sort by total reviews and select top 3
      const sortedByReviews = [...yearData].sort(
        (a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0)
      );
      const defaultSelected = sortedByReviews.slice(0, 3).map((b) => b.name);
      setCompareSelectedBusinesses(defaultSelected);

      // Assign colors to all businesses
      const colors = {};
      yearData.forEach((business, index) => {
        colors[business.name] = BUSINESS_COLORS[index % BUSINESS_COLORS.length];
      });
      setBusinessColors(colors);
    }
  }, [yearData, compareSelectedBusinesses.length]);

  // Get color for a business based on its position in selected businesses
  const getCompareColor = useCallback(
    (businessName) => {
      const selectedIndex = compareSelectedBusinesses.indexOf(businessName);
      if (selectedIndex >= 0) {
        return BUSINESS_COLORS[selectedIndex % BUSINESS_COLORS.length];
      }
      return businessColors[businessName] || BUSINESS_COLORS[0];
    },
    [businessColors, compareSelectedBusinesses]
  );

  // Handle comparison business selection
  const handleCompareBusinessSelection = (selectedOptions) => {
    setCompareSelectedBusinesses(selectedOptions.map((option) => option.value));
  };

  // Now the businessesWithYearData useMemo can use the function
  const businessesWithYearData = useMemo(() => {
    if (!reviewsData || !yearData.length) return null;

    const merged = reviewsData.map(business => {
      const yearInfo = yearData.find(yd => yd.maps_uri === business.maps_uri);
      return {
        ...business,
        historicalData: yearInfo?.data || {}
      };
    });

    return merged.sort((a, b) => {
      const growthA = calculateThreeMonthGrowth(
        a.historicalData?.monthlyReviewCounts,
        Object.keys(a.historicalData?.monthlyReviewCounts || {}).sort()
      );
      const growthB = calculateThreeMonthGrowth(
        b.historicalData?.monthlyReviewCounts,
        Object.keys(b.historicalData?.monthlyReviewCounts || {}).sort()
      );
      return growthB - growthA;
    });
  }, [reviewsData, yearData]);

  // Add a unified effect to handle all data readiness
  useEffect(() => {
    if (businessesWithYearData) {
      if (demoMode) {
        // Demo data is pre-loaded — no need to wait
        setIsLoading(false);
        setContentReady(true);
      } else {
        // Small timeout for authenticated mode to ensure smooth transition
        const timer = setTimeout(() => {
          setIsLoading(false);
          setContentReady(true);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [businessesWithYearData, demoMode]);

  // Get all businesses
  const filteredBusinesses = useMemo(() => {
    if (!businessesWithYearData) return [];
    return businessesWithYearData;
  }, [businessesWithYearData]);

  const sortedByTotal = useMemo(() => {
    if (!businessesWithYearData) return [];
    return [...businessesWithYearData].sort((a, b) => b.rating_count - a.rating_count);
  }, [businessesWithYearData]);

  const sortedBySentiment = useMemo(() => {
    if (!businessesWithYearData) return [];
    return [...businessesWithYearData].map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total = sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative;
      const sentimentScore = total > 0
        ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) / total) * 100
        : 0;
      return { ...business, sentimentScore };
    }).sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [businessesWithYearData]);

  const sortedByGrowth = useMemo(() => {
    if (!businessesWithYearData) return [];
    return [...businessesWithYearData].map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
      const threeMonthGrowth = lastThreeMonths.reduce((sum, month) => sum + monthlyData[month], 0);
      return { ...business, threeMonthGrowth };
    }).sort((a, b) => b.threeMonthGrowth - a.threeMonthGrowth);
  }, [businessesWithYearData]);

  const sortedByTraffic = useMemo(() => {
    if (!businessesWithYearData) return [];
    return [...businessesWithYearData]
      .map((business) => ({ ...business, trafficValue: getTrafficValue(business) || 0 }))
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [businessesWithYearData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(businessesWithYearData || []),
    [businessesWithYearData]
  );

  const calculateSentimentScore = (sentimentData) => {
    if (!sentimentData) return 0;
    const total = sentimentData.positive + sentimentData.neutral + sentimentData.negative;
    return total > 0 ? (sentimentData.positive / total * 100) : 0;
  };

  // Update the sortedBusinesses useMemo to sort by growth
  const sortedBusinesses = useMemo(() => {
    if (!businessesWithYearData) return [];
    
    // Sort by growth
    return [...filteredBusinesses].sort((a, b) => {
      const growthA = calculateThreeMonthGrowth(
        a.historicalData?.monthlyReviewCounts,
        Object.keys(a.historicalData?.monthlyReviewCounts || {}).sort()
      );
      const growthB = calculateThreeMonthGrowth(
        b.historicalData?.monthlyReviewCounts,
        Object.keys(b.historicalData?.monthlyReviewCounts || {}).sort()
      );
      return growthB - growthA;
    });
  }, [businessesWithYearData, filteredBusinesses]);

  // Add an effect to handle the initial load
  useEffect(() => {
    if (sortedBusinesses.length > 0 && !contentReady) {
      setContentReady(true);
    }
  }, [sortedBusinesses, contentReady]);

  // Get unique business types for filter dropdown
  const businessTypes = useMemo(() => {
    if (!reviewsData) return [];
    
    const types = new Set();
    reviewsData.forEach(business => {
      if (business.primary_type) {
        types.add(business.primary_type);
      }
    });
    
    return ['all', ...Array.from(types)];
  }, [reviewsData]);

  // Filter businesses based on search and type filter, then sort
  const filteredCardBusinesses = useMemo(() => {
    if (!sortedBusinesses.length) return [];
    
    // First filter businesses
    const filtered = sortedBusinesses.filter(business => {
      // Calculate 3-month growth for filtering
      const threeMonthGrowth = calculateThreeMonthGrowth(
        business.historicalData?.monthlyReviewCounts,
        Object.keys(business.historicalData?.monthlyReviewCounts || {}).sort()
      );

      // Check if business should be included based on 3-month growth
      const hasRecentActivity = threeMonthGrowth > 0;

      // Apply search filter
      const matchesSearch = searchQuery === '' || 
        business.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Apply type filter
      const matchesType = selectedType === 'all' || 
        business.primary_type === selectedType;
      
      // Business must have recent activity and match search/type filters
      return hasRecentActivity && matchesSearch && matchesType;
    });
    
    // Then sort based on selected criteria
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'rating_count':
          return b.rating_count - a.rating_count;
        case 'rating_avg':
          return b.rating_avg - a.rating_avg;
        case 'growth':
        default:
          const growthA = calculateThreeMonthGrowth(
            a.historicalData?.monthlyReviewCounts,
            Object.keys(a.historicalData?.monthlyReviewCounts || {}).sort()
          );
          const growthB = calculateThreeMonthGrowth(
            b.historicalData?.monthlyReviewCounts,
            Object.keys(b.historicalData?.monthlyReviewCounts || {}).sort()
          );
          return growthB - growthA;
      }
    });
  }, [sortedBusinesses, searchQuery, selectedType, sortBy]);
  
  // Update the insights calculation
  const insights = useMemo(() => {
    if (!sortedBusinesses || !sortedBusinesses.length) return null;

    // Find top rated business
    const topRated = [...sortedBusinesses]
      .filter(b => b && typeof b.rating_avg === 'number')
      .sort((a, b) => b.rating_avg - a.rating_avg)[0];

    // Find most reviewed business
    const mostReviewed = [...sortedBusinesses]
      .filter(b => b && typeof b.rating_count === 'number')
      .sort((a, b) => b.rating_count - a.rating_count)[0];

    // Find highest growth business
    const highestGrowth = [...sortedBusinesses]
      .filter(b => b && b.historicalData?.monthlyReviewCounts)
      .sort((a, b) => {
        const aMonths = Object.keys(a.historicalData?.monthlyReviewCounts || {}).sort();
        const bMonths = Object.keys(b.historicalData?.monthlyReviewCounts || {}).sort();
        
        const aGrowth = calculateThreeMonthGrowth(a.historicalData?.monthlyReviewCounts, aMonths);
        const bGrowth = calculateThreeMonthGrowth(b.historicalData?.monthlyReviewCounts, bMonths);
        
        return bGrowth - aGrowth;
      })[0];

    // Find highest sentiment business
    const highestSentiment = [...sortedBusinesses]
      .filter(b => b && b.historicalData?.sentimentBreakdown)
      .sort((a, b) => {
        const aSentiment = calculateSentimentScore(a.historicalData?.sentimentBreakdown);
        const bSentiment = calculateSentimentScore(b.historicalData?.sentimentBreakdown);
        return bSentiment - aSentiment;
      })[0];

    if (!topRated || !mostReviewed || !highestGrowth || !highestSentiment) return null;

    return {
      topRated: {
        name: topRated.name,
        value: topRated.rating_avg.toFixed(1)
      },
      mostReviewed: {
        name: mostReviewed.name,
        value: mostReviewed.rating_count.toLocaleString()
      },
      highestGrowth: {
        name: highestGrowth.name,
        value: calculateThreeMonthGrowth(
          highestGrowth.historicalData?.monthlyReviewCounts,
          Object.keys(highestGrowth.historicalData?.monthlyReviewCounts || {}).sort()
        ).toFixed(0)
      },
      highestSentiment: {
        name: highestSentiment.name,
        value: (calculateSentimentScore(highestSentiment.historicalData?.sentimentBreakdown) * 100).toFixed(0)
      }
    };
  }, [sortedBusinesses]);

  // ============================================
  // COMPARISON SECTION DATA PROCESSING
  // ============================================

  // Business select options for comparison
  const compareSelectOptions = useMemo(() => {
    if (!yearData.length) return [];

    return yearData
      .sort((a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0))
      .map((business) => ({
        value: business.name,
        label: `${business.name} (${business.data?.totalReviews || 0} reviews)`,
      }));
  }, [yearData]);

  // Get selected businesses data from yearData for comparison
  const compareSelectedBusinessesData = useMemo(() => {
    return yearData.filter((business) =>
      compareSelectedBusinesses.includes(business.name)
    );
  }, [yearData, compareSelectedBusinesses]);

  // Calculate rankings among selected businesses
  const businessRankings = useMemo(() => {
    if (!compareSelectedBusinessesData.length) return {};

    const rankings = {};
    const metrics = ['totalReviews', 'averageRating'];
    
    metrics.forEach((metric) => {
      const sorted = [...compareSelectedBusinessesData].sort((a, b) => {
        const aVal = a.data?.[metric] || 0;
        const bVal = b.data?.[metric] || 0;
        return bVal - aVal;
      });
      sorted.forEach((business, index) => {
        if (!rankings[business.name]) rankings[business.name] = {};
        rankings[business.name][metric] = index + 1;
      });
    });

    // Sentiment ranking (by positive percentage)
    const sentimentSorted = [...compareSelectedBusinessesData].sort((a, b) => {
      const aS = a.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const bS = b.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const aTotal = aS.positive + aS.neutral + aS.negative;
      const bTotal = bS.positive + bS.neutral + bS.negative;
      const aPercent = aTotal > 0 ? aS.positive / aTotal : 0;
      const bPercent = bTotal > 0 ? bS.positive / bTotal : 0;
      return bPercent - aPercent;
    });
    sentimentSorted.forEach((business, index) => {
      if (!rankings[business.name]) rankings[business.name] = {};
      rankings[business.name].sentiment = index + 1;
    });

    return rankings;
  }, [compareSelectedBusinessesData]);

  // Calculate review growth data for each business (last month vs previous month)
  const compareReviewGrowthData = useMemo(() => {
    const growthData = {};
    
    compareSelectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      const sortedMonths = Object.keys(monthlyData).sort((a, b) => new Date(b) - new Date(a));
      
      if (sortedMonths.length >= 1) {
        const lastMonth = sortedMonths[0];
        const lastMonthReviews = monthlyData[lastMonth] || 0;
        const previousMonthReviews = sortedMonths.length >= 2 ? (monthlyData[sortedMonths[1]] || 0) : 0;
        
        let growthPercent = 0;
        if (previousMonthReviews > 0) {
          growthPercent = ((lastMonthReviews - previousMonthReviews) / previousMonthReviews) * 100;
        } else if (lastMonthReviews > 0) {
          growthPercent = 100;
        }
        
        growthData[business.name] = {
          lastMonth: lastMonth,
          lastMonthReviews: lastMonthReviews,
          previousMonthReviews: previousMonthReviews,
          change: lastMonthReviews - previousMonthReviews,
          growthPercent: growthPercent,
          isPositive: lastMonthReviews >= previousMonthReviews,
        };
      } else {
        growthData[business.name] = {
          lastMonth: null,
          lastMonthReviews: 0,
          previousMonthReviews: 0,
          change: 0,
          growthPercent: 0,
          isPositive: true,
        };
      }
    });
    
    return growthData;
  }, [compareSelectedBusinessesData]);

  // Reviews Over Time Data (Line Chart) for comparison
  const compareReviewsOverTimeData = useMemo(() => {
    if (!compareSelectedBusinessesData.length) return [];

    const allMonths = new Set();
    compareSelectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      Object.keys(monthlyData).forEach((month) => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    return sortedMonths.map((month) => {
      const dataPoint = { month };
      compareSelectedBusinessesData.forEach((business) => {
        dataPoint[business.name] =
          business.data?.monthlyReviewCounts?.[month] || 0;
      });
      return dataPoint;
    });
  }, [compareSelectedBusinessesData]);

  // Total Reviews Over Time Data (Cumulative) for comparison
  const compareTotalReviewsOverTimeData = useMemo(() => {
    if (!compareSelectedBusinessesData.length) return [];

    const allMonths = new Set();
    compareSelectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      Object.keys(monthlyData).forEach((month) => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    if (sortedMonths.length === 0) return [];

    // Calculate starting total for each business
    const startingTotals = {};
    compareSelectedBusinessesData.forEach((business) => {
      const currentData = reviewsData?.find(
        (r) => r.maps_uri === business.maps_uri || r.name === business.name
      );
      const currentTotal = currentData?.rating_count || business.data?.totalReviews || 0;
      const monthlyData = business.data?.monthlyReviewCounts || {};
      
      let reviewsInRange = 0;
      sortedMonths.forEach((month) => {
        reviewsInRange += monthlyData[month] || 0;
      });
      
      startingTotals[business.name] = Math.max(0, currentTotal - reviewsInRange);
    });

    const runningTotals = { ...startingTotals };

    return sortedMonths.map((month) => {
      const dataPoint = { month };
      
      compareSelectedBusinessesData.forEach((business) => {
        const monthlyData = business.data?.monthlyReviewCounts || {};
        runningTotals[business.name] += monthlyData[month] || 0;
        dataPoint[business.name] = runningTotals[business.name];
      });

      return dataPoint;
    });
  }, [compareSelectedBusinessesData, reviewsData]);

  // Customer Sentiment Data for comparison
  const compareSentimentData = useMemo(() => {
    return compareSelectedBusinessesData.map((business) => {
      const sentiment = business.data?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0,
      };
      const total = sentiment.positive + sentiment.neutral + sentiment.negative;
      return {
        name: business.name.length > 20 ? business.name.substring(0, 20) + '...' : business.name,
        fullName: business.name,
        positive: sentiment.positive,
        neutral: sentiment.neutral,
        negative: sentiment.negative,
        total,
        positivePercent: total > 0 ? ((sentiment.positive / total) * 100).toFixed(1) : 0,
        neutralPercent: total > 0 ? ((sentiment.neutral / total) * 100).toFixed(1) : 0,
        negativePercent: total > 0 ? ((sentiment.negative / total) * 100).toFixed(1) : 0,
      };
    });
  }, [compareSelectedBusinessesData]);

  // Radar chart data for overall comparison
  const compareRadarData = useMemo(() => {
    if (!compareSelectedBusinessesData.length) return [];

    const maxReviews = Math.max(...compareSelectedBusinessesData.map(b => b.data?.totalReviews || 0));

    const metrics = [
      { metric: 'Reviews', key: 'reviews' },
      { metric: 'Rating', key: 'rating' },
      { metric: 'Sentiment', key: 'sentiment' },
      { metric: 'Growth', key: 'growth' },
      { metric: 'Consistency', key: 'consistency' },
    ];

    return metrics.map((m) => {
      const dataPoint = { metric: m.metric };
      compareSelectedBusinessesData.forEach((business) => {
        let value = 0;
        const sentiment = business.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
        const sentTotal = sentiment.positive + sentiment.neutral + sentiment.negative;
        const monthly = Object.values(business.data?.monthlyReviewCounts || {});

        switch (m.key) {
          case 'reviews':
            value = maxReviews > 0 ? ((business.data?.totalReviews || 0) / maxReviews) * 100 : 0;
            break;
          case 'rating':
            value = ((business.data?.averageRating || 0) / 5) * 100;
            break;
          case 'sentiment':
            value = sentTotal > 0 ? (sentiment.positive / sentTotal) * 100 : 0;
            break;
          case 'growth':
            if (monthly.length >= 2) {
              const recent = monthly.slice(-3).reduce((a, b) => a + b, 0) / 3;
              const older = monthly.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
              value = older > 0 ? Math.min(((recent / older) * 50), 100) : 50;
            } else {
              value = 50;
            }
            break;
          case 'consistency':
            if (monthly.length > 0) {
              const avg = monthly.reduce((a, b) => a + b, 0) / monthly.length;
              const variance = monthly.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / monthly.length;
              const stdDev = Math.sqrt(variance);
              const cv = avg > 0 ? stdDev / avg : 0;
              value = Math.max(0, 100 - (cv * 100));
            }
            break;
          default:
            value = 0;
        }
        dataPoint[business.name] = Math.round(value);
      });
      return dataPoint;
    });
  }, [compareSelectedBusinessesData]);

  // SWOT Comparison Data - extract strengths and weaknesses for selected businesses
  const compareSwotData = useMemo(() => {
    if (!compareSelectedBusinesses.length || !reviewsData?.length) return [];

    return compareSelectedBusinesses
      .map((businessName) => {
        const businessData = reviewsData.find(
          (b) => b.name === businessName
        );

        if (!businessData?.versus_insights) {
          return null;
        }

        const swotSections = parseSwotSections(businessData.versus_insights);

        if (!swotSections.strengths && !swotSections.weaknesses) {
          return null;
        }

        return {
          name: businessName,
          shortName: businessName.length > 25 ? businessName.substring(0, 25) + '...' : businessName,
          strengths: swotSections.strengths,
          weaknesses: swotSections.weaknesses,
          color: getCompareColor(businessName),
          maps_uri: businessData.maps_uri,
        };
      })
      .filter(Boolean);
  }, [compareSelectedBusinesses, reviewsData, getCompareColor]);

  // Format month for display
  const formatMonth = (dateString) => {
    const date = new Date(dateString + '-01T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Get rank icon
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return FaCrown;
      case 2:
        return FaMedal;
      case 3:
        return FaAward;
      default:
        return null;
    }
  };

  // Get rank color
  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return 'yellow.400';
      case 2:
        return 'gray.400';
      case 3:
        return 'orange.400';
      default:
        return 'gray.300';
    }
  };

  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box p={4}>
        <Text>No business data available</Text>
      </Box>
    );
  }

  if (isLoading || !contentReady) {
    return (
      <Flex justify="center" align="center" minH="500px" width="100%">
        <VStack spacing={4}>
          <Spinner 
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
          <Text color="gray.600" fontSize="lg">Loading business insights...</Text>
        </VStack>
      </Flex>
    );
  }

  const handleBusinessToggle = (business) => {
    setSelectedBusiness(business);
    setIsModalOpen(true);
  };

  // Remove unused selection functions

  const SectionHeading = ({ children }) => (
    <HStack spacing={2} mb={4} mt={6}>
      <Divider flex={1} borderColor="gray.300" />
      <Text color="gray.600" fontSize="lg" fontWeight="bold" whiteSpace="nowrap">
        {children}
      </Text>
      <Divider flex={1} borderColor="gray.300" />
    </HStack>
  );

  // Helper function to get business initials (up to 2 characters)
  const getBusinessInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    // Get first letter of first two significant words (skip common words like "and", "the", "a")
    const skipWords = ['and', 'the', 'a', 'an', 'of', 'in', 'for', '&'];
    const significantWords = words.filter(w => !skipWords.includes(w.toLowerCase()));
    if (significantWords.length >= 2) {
      return (significantWords[0][0] + significantWords[1][0]).toUpperCase();
    }
    if (significantWords.length === 1) {
      return significantWords[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + (words[1] ? words[1][0] : '')).toUpperCase();
  };

  const MetricCard = ({ icon, label, value, change }) => {
    const bgColor = useColorModeValue('gray.50', 'gray.700');
    const isPositive = change?.startsWith('+');

    return (
      <Box p={4} bg={bgColor} borderRadius="lg">
        <VStack spacing={1} align="flex-start">
          <Icon as={icon} color="blue.500" />
          <Text fontSize="sm" color="gray.500">{label}</Text>
          <HStack spacing={2} align="baseline">
            <Text fontSize="xl" fontWeight="bold">{value}</Text>
            {change && (
              <Text 
                fontSize="sm" 
                color={isPositive ? "green.500" : "red.500"}
              >
                {change}
              </Text>
            )}
          </HStack>
        </VStack>
      </Box>
    );
  };

  const PerformanceBar = ({ label, value, colorScheme }) => (
    <Box w="100%">
      <Flex justify="space-between" mb={1}>
        <Text fontSize="sm">{label}</Text>
        <Text fontSize="sm" fontWeight="bold">{value}%</Text>
      </Flex>
      <Progress 
        value={value} 
        colorScheme={colorScheme} 
        borderRadius="full" 
        size="sm"
      />
    </Box>
  );

  // CompetitiveAnalysisCard component
  const CompetitiveAnalysisCard = ({ business }) => {
    // Component code...
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Enhanced Search and Filter Section */}
        <Box 
          bg={cardBg}
          p={6}
          borderRadius="xl"
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <VStack spacing={4}>
            <HStack spacing={3} w="full" justify="center">
              <Icon as={FaSearch} color="blue.500" boxSize={6} />
              <VStack align="center" spacing={1}>
                <Heading size="md" color="gray.700">Business Intelligence Search</Heading>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Find and analyze businesses with advanced filtering and sorting
                </Text>
              </VStack>
            </HStack>
            
          <Flex 
            spacing={4} 
            align="center" 
            w="full" 
            maxW="900px" 
            mx="auto" 
            gap={4}
          >
            <InputGroup size="lg" flex="1">
              <InputLeftElement pointerEvents="none" h="full">
                <Icon as={FaSearch} color="gray.400" boxSize={5} />
              </InputLeftElement>
              <Input
                placeholder="Search businesses by name or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={searchBg}
                borderColor={searchBorder}
                fontSize="lg"
                h="56px"
                  borderRadius="lg"
                  _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
              />
            </InputGroup>

            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              bg={searchBg}
              borderColor={searchBorder}
              size="lg"
              h="56px"
              fontSize="lg"
              width="auto"
              minW="180px"
                borderRadius="lg"
                _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
              _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
            >
              <option value="growth">Hottest 🔥</option>
              <option value="rating_count">Most Popular 👥</option>
              <option value="rating_avg">Top Rated ⭐</option>
            </Select>
          </Flex>
          </VStack>
        </Box>

        {/* Enhanced Business Cards Section with collapsible toggle */}
        <Box 
          bg={cardBg} 
          p={6} 
          borderRadius="xl" 
          shadow="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          {/* Background gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bgGradient="linear(to-r, blue.400, purple.400, pink.400)"
          />
          
          <Flex justify="space-between" align="center" mb={6}>
            <HStack spacing={3}>
              <Icon as={FaLightbulb} color="blue.500" boxSize={6} />
              <VStack align="start" spacing={1}>
                <Heading size="md" color="gray.700">Business Intelligence Cards</Heading>
                <Text fontSize="sm" color="gray.500">
                  Detailed insights and competitive analysis for each business
                </Text>
              </VStack>
            </HStack>
            <Button 
              size="sm" 
              rightIcon={isCardsExpanded ? <FaChevronUp /> : <FaChevronDown />}
              onClick={() => setIsCardsExpanded(!isCardsExpanded)}
              variant="ghost"
              colorScheme="blue"
              _hover={{ bg: "blue.50" }}
            >
              {isCardsExpanded ? "Collapse" : "Expand"}
            </Button>
          </Flex>
          
          <Collapse in={isCardsExpanded} animateOpacity>
            {/* Business Cards Grid */}
            {!contentReady ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner 
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Flex>
            ) : (
              <Box maxW="100%" mx="auto" mb={8} pt={2} position="relative">
                <SimpleGrid 
                  columns={{ 
                    base: 1, 
                    md: filteredCardBusinesses.length === 1 ? 1 : 2, 
                    lg: filteredCardBusinesses.length === 1 ? 1 : (filteredCardBusinesses.length === 2 ? 2 : 3), 
                    xl: filteredCardBusinesses.length < 4 ? filteredCardBusinesses.length : 4 
                  }} 
                  spacing={6} 
                  maxW="100%" 
                  alignItems="stretch"
                  sx={{
                    '& > *': {
                      transition: 'all 0.3s ease-in-out',
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                    '@keyframes fadeInScale': {
                      '0%': {
                        opacity: 0,
                        transform: 'scale(0.95)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                    },
                    '& > *:not(:first-of-type)': {
                      animation: 'fadeInScale 0.3s ease-in-out',
                    }
                  }}
                >
                  {filteredCardBusinesses.length ? (
                    filteredCardBusinesses.map((business, index) => (
                      <Box 
                        key={business.maps_uri}
                        ml={filteredCardBusinesses.length === 1 ? 0 : 'auto'}
                        mr={filteredCardBusinesses.length === 1 ? 'auto' : 'auto'}
                      >
                        <BusinessIntelCard
                          business={business}
                          trafficData={trafficHistory[business.maps_uri]}
                          onClick={() => handleBusinessToggle(business)}
                          variant="card"
                        />
                      </Box>
                    ))
                  ) : (
                    <Flex justify="center" width="100%" p={8} gridColumn="1 / -1">
                      <Text color="gray.500">No businesses match your search criteria.</Text>
                    </Flex>
                  )}
                </SimpleGrid>
              </Box>
            )}
          </Collapse>
        </Box>

        {/* ============================================ */}
        {/* COMPARE BUSINESSES SECTION */}
        {/* ============================================ */}
        <Box 
          bg={cardBg} 
          p={6} 
          borderRadius="xl" 
          shadow="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          {/* Background gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bgGradient="linear(to-r, blue.500, purple.500)"
          />
          
          <Flex justify="space-between" align="center" mb={6}>
            <HStack spacing={3}>
              <Icon as={FaBalanceScale} color="blue.500" boxSize={6} />
              <VStack align="start" spacing={1}>
                <Heading size="md" color="gray.700">Compare Businesses</Heading>
                <Text fontSize="sm" color="gray.500">
                  Side-by-side competitive analysis with detailed metrics and charts
                </Text>
              </VStack>
            </HStack>
            <HStack spacing={2}>
              {compareSelectedBusinesses.length > 0 && (
                <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                  {compareSelectedBusinesses.length} selected
                </Badge>
              )}
              <Button 
                size="sm" 
                rightIcon={isCompareExpanded ? <FaChevronUp /> : <FaChevronDown />}
                onClick={() => setIsCompareExpanded(!isCompareExpanded)}
                variant="ghost"
                colorScheme="blue"
                _hover={{ bg: "blue.50" }}
              >
                {isCompareExpanded ? "Collapse" : "Expand"}
              </Button>
            </HStack>
          </Flex>

          <Collapse in={isCompareExpanded} animateOpacity>
            <VStack spacing={6} align="stretch">
              {/* Business Selection */}
              <Box
                bg={statBg}
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor={borderColor}
              >
                <HStack spacing={3} mb={3} align="center">
                  <Icon as={FaUsers} color={accentColor} boxSize={5} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" color={textColor}>
                      Select Businesses to Compare
                    </Text>
                    <Text fontSize="sm" color={subtitleColor}>
                      Choose up to 5 businesses for optimal comparison
                    </Text>
                  </VStack>
                </HStack>
                  
                <Select
                  isMulti
                  name="compare-businesses"
                  options={compareSelectOptions}
                  placeholder="Search and select businesses..."
                  closeMenuOnSelect={false}
                  onChange={handleCompareBusinessSelection}
                  value={compareSelectOptions.filter((option) =>
                    compareSelectedBusinesses.includes(option.value)
                  )}
                  chakraStyles={{
                    container: (provided) => ({
                      ...provided,
                      width: '100%',
                    }),
                    control: (provided) => ({
                      ...provided,
                      borderRadius: 'lg',
                      borderColor: borderColor,
                      bg: cardBg,
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      background: 'transparent',
                      border: 'none',
                    }),
                    multiValue: (provided, state) => ({
                      ...provided,
                      backgroundColor: getCompareColor(state.data.value),
                      color: 'white',
                      borderRadius: 'md',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: 'white',
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'whiteAlpha.300',
                        color: 'white',
                      },
                    }),
                  }}
                />
              </Box>

              {compareSelectedBusinesses.length === 0 ? (
                <Box
                  bg={cardBg}
                  borderRadius="lg"
                  p={8}
                  textAlign="center"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Icon as={FaChartBar} boxSize={12} color="gray.300" mb={4} />
                  <Heading size="md" color={subtitleColor} mb={2}>
                    No Businesses Selected
                  </Heading>
                  <Text color={subtitleColor}>
                    Please select at least one business above to view comparison analytics.
                  </Text>
                </Box>
              ) : (
                <VStack spacing={6} align="stretch">
                  {/* Quick Stats Grid */}
                  <SimpleGrid columns={{ base: 1, md: 2, lg: Math.min(compareSelectedBusinesses.length, 4) }} spacing={4}>
                    {compareSelectedBusinessesData.map((business) => {
                      const sentiment = business.data?.sentimentBreakdown || {
                        positive: 0,
                        neutral: 0,
                        negative: 0,
                      };
                      const total = sentiment.positive + sentiment.neutral + sentiment.negative;
                      const positivePercent = total > 0 ? ((sentiment.positive / total) * 100).toFixed(0) : 0;
                      const rank = businessRankings[business.name]?.totalReviews || 0;
                      const RankIcon = getRankIcon(rank);
                      const imageUrl = business.maps_uri ? businessImages[business.maps_uri] : null;

                      return (
                        <Box
                          key={business.name}
                          bg={cardBg}
                          borderRadius="lg"
                          overflow="hidden"
                          shadow="md"
                          border="1px solid"
                          borderColor={borderColor}
                          position="relative"
                          transition="all 0.2s"
                          _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                        >
                          {/* Colored top bar */}
                          <Box
                            h="3px"
                            bg={getCompareColor(business.name)}
                          />
                          
                          <Box p={5}>
                            <HStack justify="space-between" mb={4}>
                              <HStack spacing={3}>
                                <Avatar
                                  size="md"
                                  name={business.name}
                                  src={imageUrl}
                                  bg={getCompareColor(business.name)}
                                  color="white"
                                />
                                <VStack align="start" spacing={0}>
                                  <Text
                                    fontWeight="bold"
                                    fontSize="md"
                                    color={textColor}
                                    noOfLines={1}
                                    maxW="180px"
                                  >
                                    {business.name}
                                  </Text>
                                  <Flex align="center" gap={1}>
                                    <Icon as={FaStar} color="yellow.400" boxSize="12px" position="relative" top="2px" />
                                    <Text fontSize="sm" color={subtitleColor}>
                                      {business.data?.averageRating?.toFixed(1) || 'N/A'}
                                    </Text>
                                  </Flex>
                                </VStack>
                              </HStack>
                              {RankIcon && (
                                <ChakraTooltip label={`#${rank} in reviews`} placement="top">
                                  <Circle size="32px" bg={getRankColor(rank)} color="white">
                                    <Icon as={RankIcon} boxSize={4} />
                                  </Circle>
                                </ChakraTooltip>
                              )}
                            </HStack>

                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                              <Stat size="sm">
                                <StatLabel fontSize="xs" color={subtitleColor}>
                                  Total Reviews
                                </StatLabel>
                                <StatNumber fontSize="xl" color={textColor}>
                                  {(business.data?.totalReviews || 0).toLocaleString()}
                                </StatNumber>
                              </Stat>
                              <Stat size="sm">
                                <StatLabel fontSize="xs" color={subtitleColor}>
                                  Positive
                                </StatLabel>
                                <StatNumber fontSize="xl" color="green.500">
                                  {positivePercent}%
                                </StatNumber>
                              </Stat>
                            </SimpleGrid>

                            {/* Sentiment Bar */}
                            <Box mt={4}>
                              <HStack spacing={1} h="8px" borderRadius="full" overflow="hidden">
                                <Box
                                  bg={SENTIMENT_COLORS.positive}
                                  h="full"
                                  flex={sentiment.positive || 1}
                                  transition="all 0.3s"
                                />
                                <Box
                                  bg={SENTIMENT_COLORS.neutral}
                                  h="full"
                                  flex={sentiment.neutral || 1}
                                  transition="all 0.3s"
                                />
                                <Box
                                  bg={SENTIMENT_COLORS.negative}
                                  h="full"
                                  flex={sentiment.negative || 1}
                                  transition="all 0.3s"
                                />
                              </HStack>
                              <HStack justify="space-between" mt={2}>
                                <HStack spacing={1}>
                                  <Circle size="8px" bg={SENTIMENT_COLORS.positive} />
                                  <Text fontSize="xs" color={subtitleColor}>
                                    {sentiment.positive}
                                  </Text>
                                </HStack>
                                <HStack spacing={1}>
                                  <Circle size="8px" bg={SENTIMENT_COLORS.neutral} />
                                  <Text fontSize="xs" color={subtitleColor}>
                                    {sentiment.neutral}
                                  </Text>
                                </HStack>
                                <HStack spacing={1}>
                                  <Circle size="8px" bg={SENTIMENT_COLORS.negative} />
                                  <Text fontSize="xs" color={subtitleColor}>
                                    {sentiment.negative}
                                  </Text>
                                </HStack>
                              </HStack>
                            </Box>

                            {/* Review Growth Last Month */}
                            {compareReviewGrowthData[business.name] && (
                              <Box
                                mt={3}
                                pt={3}
                                borderTop="1px solid"
                                borderColor={borderColor}
                              >
                                <Text fontSize="xs" color={subtitleColor} fontWeight="medium" mb={2}>
                                  Review Growth Last Month
                                  {compareReviewGrowthData[business.name].lastMonth && (
                                    <Text as="span" fontWeight="normal"> · {new Date(compareReviewGrowthData[business.name].lastMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</Text>
                                  )}
                                </Text>
                                <HStack spacing={3}>
                                  <HStack spacing={1}>
                                    <Text fontSize="xl" fontWeight="bold" color={textColor}>
                                      +{compareReviewGrowthData[business.name].lastMonthReviews}
                                    </Text>
                                    <Text fontSize="xs" color={subtitleColor}>
                                      reviews
                                    </Text>
                                  </HStack>
                                  {compareReviewGrowthData[business.name].previousMonthReviews > 0 && (
                                    <HStack
                                      spacing={1}
                                      px={2}
                                      py={0.5}
                                      borderRadius="full"
                                      bg={compareReviewGrowthData[business.name].isPositive ? 'green.50' : 'red.50'}
                                    >
                                      <Icon
                                        as={compareReviewGrowthData[business.name].isPositive ? FaArrowUp : FaArrowDown}
                                        color={compareReviewGrowthData[business.name].isPositive ? 'green.500' : 'red.500'}
                                        boxSize={2.5}
                                      />
                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color={compareReviewGrowthData[business.name].isPositive ? 'green.600' : 'red.600'}
                                      >
                                        {Math.abs(compareReviewGrowthData[business.name].growthPercent).toFixed(0)}%
                                      </Text>
                                    </HStack>
                                  )}
                                </HStack>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </SimpleGrid>

                  {/* Charts Grid */}
                  <Grid
                    templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
                    gap={6}
                  >
                    {/* Reviews Over Time */}
                    <GridItem colSpan={{ base: 1, lg: 2 }}>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        p={6}
                        shadow="lg"
                        border="1px solid"
                        borderColor={borderColor}
                      >
                        <HStack spacing={3} mb={6}>
                          <Circle size="40px" bg="blue.100" color="blue.600">
                            <Icon as={FaChartLine} boxSize={4} />
                          </Circle>
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color={textColor}>
                              Reviews Over Time
                            </Heading>
                            <Text fontSize="sm" color={subtitleColor}>
                              Monthly review volume trends for selected businesses
                            </Text>
                          </VStack>
                        </HStack>
                        
                        <Box height={{ base: "280px", md: "400px" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={compareReviewsOverTimeData}
                              margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                            >
                              <defs>
                                {compareSelectedBusinessesData.map((business) => (
                                  <linearGradient
                                    key={`gradient-${business.name}`}
                                    id={`compare-color-${business.name.replace(/\s/g, '')}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop
                                      offset="5%"
                                      stopColor={getCompareColor(business.name)}
                                      stopOpacity={0.3}
                                    />
                                    <stop
                                      offset="95%"
                                      stopColor={getCompareColor(business.name)}
                                      stopOpacity={0}
                                    />
                                  </linearGradient>
                                ))}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                              <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                                axisLine={{ stroke: '#E2E8F0' }}
                              />
                              <YAxis
                                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                                axisLine={{ stroke: '#E2E8F0' }}
                              />
                              <RechartsTooltip
                                labelFormatter={formatMonth}
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                  padding: '12px 16px',
                                }}
                              />
                              <Legend
                                wrapperStyle={{ paddingTop: '20px' }}
                                iconType="circle"
                              />
                              {compareSelectedBusinessesData.map((business) => (
                                <Area
                                  key={business.name}
                                  type="monotone"
                                  dataKey={business.name}
                                  stroke={getCompareColor(business.name)}
                                  strokeWidth={2.5}
                                  fill={`url(#compare-color-${business.name.replace(/\s/g, '')})`}
                                  dot={{ fill: getCompareColor(business.name), strokeWidth: 2, r: 4 }}
                                  activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
                                />
                              ))}
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </GridItem>

                    {/* Total Reviews Over Time (Cumulative) */}
                    <GridItem colSpan={{ base: 1, lg: 2 }}>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        p={6}
                        shadow="lg"
                        border="1px solid"
                        borderColor={borderColor}
                      >
                        <HStack spacing={3} mb={6}>
                          <Circle size="40px" bg="blue.100" color="blue.600">
                            <Icon as={FaChartLine} boxSize={4} />
                          </Circle>
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color={textColor}>
                              Total Reviews Over Time
                            </Heading>
                            <Text fontSize="sm" color={subtitleColor}>
                              Cumulative review count trends for selected businesses
                            </Text>
                          </VStack>
                        </HStack>
                        
                        <Box height={{ base: "280px", md: "400px" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={compareTotalReviewsOverTimeData}
                              margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                            >
                              <defs>
                                {compareSelectedBusinessesData.map((business) => (
                                  <linearGradient
                                    key={`total-gradient-${business.name}`}
                                    id={`compare-total-color-${business.name.replace(/\s/g, '')}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop
                                      offset="5%"
                                      stopColor={getCompareColor(business.name)}
                                      stopOpacity={0.3}
                                    />
                                    <stop
                                      offset="95%"
                                      stopColor={getCompareColor(business.name)}
                                      stopOpacity={0}
                                    />
                                  </linearGradient>
                                ))}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                              <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                                axisLine={{ stroke: '#E2E8F0' }}
                              />
                              <YAxis
                                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                                axisLine={{ stroke: '#E2E8F0' }}
                              />
                              <RechartsTooltip
                                labelFormatter={formatMonth}
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                  padding: '12px 16px',
                                }}
                                formatter={(value, name) => [value.toLocaleString(), name]}
                              />
                              <Legend
                                wrapperStyle={{ paddingTop: '20px' }}
                                iconType="circle"
                              />
                              {compareSelectedBusinessesData.map((business) => (
                                <Area
                                  key={business.name}
                                  type="monotone"
                                  dataKey={business.name}
                                  stroke={getCompareColor(business.name)}
                                  strokeWidth={2.5}
                                  fill={`url(#compare-total-color-${business.name.replace(/\s/g, '')})`}
                                  dot={{ fill: getCompareColor(business.name), strokeWidth: 2, r: 4 }}
                                  activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
                                />
                              ))}
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </GridItem>

                    {/* Performance Radar */}
                    <GridItem>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        p={6}
                        shadow="lg"
                        border="1px solid"
                        borderColor={borderColor}
                        height="100%"
                      >
                        <HStack spacing={3} mb={6}>
                          <Circle size="40px" bg="purple.100" color="purple.600">
                            <Icon as={FaTrophy} boxSize={4} />
                          </Circle>
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color={textColor}>
                              Performance Overview
                            </Heading>
                            <Text fontSize="sm" color={subtitleColor}>
                              Multi-dimensional comparison
                            </Text>
                          </VStack>
                        </HStack>
                        
                        <Box height="350px">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={compareRadarData}>
                              <PolarGrid stroke="#E2E8F0" />
                              <PolarAngleAxis
                                dataKey="metric"
                                tick={{ fill: '#4A5568', fontSize: 14, fontWeight: 600 }}
                              />
                              <PolarRadiusAxis
                                angle={30}
                                domain={[0, 100]}
                                tick={{ fill: '#718096', fontSize: 10 }}
                              />
                              {compareSelectedBusinessesData.map((business) => (
                                <Radar
                                  key={business.name}
                                  name={business.name}
                                  dataKey={business.name}
                                  stroke={getCompareColor(business.name)}
                                  fill={getCompareColor(business.name)}
                                  fillOpacity={0.2}
                                  strokeWidth={2}
                                />
                              ))}
                              <Legend iconType="circle" />
                              <RechartsTooltip
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                }}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </GridItem>

                    {/* Sentiment Comparison */}
                    <GridItem>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        p={6}
                        shadow="lg"
                        border="1px solid"
                        borderColor={borderColor}
                        height="100%"
                      >
                        <HStack spacing={3} mb={6}>
                          <Circle size="40px" bg="green.100" color="green.600">
                            <Icon as={FaThumbsUp} boxSize={4} />
                          </Circle>
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color={textColor}>
                              Sentiment Analysis
                            </Heading>
                            <Text fontSize="sm" color={subtitleColor}>
                              Customer satisfaction breakdown
                            </Text>
                          </VStack>
                        </HStack>
                        
                        <Box height="350px">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={compareSentimentData}
                              layout="vertical"
                              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                              <XAxis type="number" tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }} />
                              <YAxis
                                dataKey="name"
                                type="category"
                                width={100}
                                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                              />
                              <RechartsTooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <Box
                                        bg="white"
                                        p={4}
                                        boxShadow="xl"
                                        borderRadius="xl"
                                        border="1px solid"
                                        borderColor="gray.100"
                                      >
                                        <Text fontWeight="bold" mb={3} fontSize="sm">
                                          {data.fullName}
                                        </Text>
                                        <VStack align="stretch" spacing={2}>
                                          <HStack justify="space-between">
                                            <HStack>
                                              <Circle size="10px" bg={SENTIMENT_COLORS.positive} />
                                              <Text fontSize="sm">Positive</Text>
                                            </HStack>
                                            <Text fontWeight="semibold" fontSize="sm">
                                              {data.positive} ({data.positivePercent}%)
                                            </Text>
                                          </HStack>
                                          <HStack justify="space-between">
                                            <HStack>
                                              <Circle size="10px" bg={SENTIMENT_COLORS.neutral} />
                                              <Text fontSize="sm">Neutral</Text>
                                            </HStack>
                                            <Text fontWeight="semibold" fontSize="sm">
                                              {data.neutral} ({data.neutralPercent}%)
                                            </Text>
                                          </HStack>
                                          <HStack justify="space-between">
                                            <HStack>
                                              <Circle size="10px" bg={SENTIMENT_COLORS.negative} />
                                              <Text fontSize="sm">Negative</Text>
                                            </HStack>
                                            <Text fontWeight="semibold" fontSize="sm">
                                              {data.negative} ({data.negativePercent}%)
                                            </Text>
                                          </HStack>
                                        </VStack>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Legend iconType="circle" />
                              <Bar
                                dataKey="positive"
                                stackId="a"
                                fill={SENTIMENT_COLORS.positive}
                                name="Positive"
                                radius={[0, 0, 0, 0]}
                              />
                              <Bar
                                dataKey="neutral"
                                stackId="a"
                                fill={SENTIMENT_COLORS.neutral}
                                name="Neutral"
                              />
                              <Bar
                                dataKey="negative"
                                stackId="a"
                                fill={SENTIMENT_COLORS.negative}
                                name="Negative"
                                radius={[0, 4, 4, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </GridItem>
                  </Grid>

                  {/* Detailed Comparison Table */}
                  <Box
                    bg={cardBg}
                    borderRadius="lg"
                    p={6}
                    shadow="lg"
                    border="1px solid"
                    borderColor={borderColor}
                    overflowX="auto"
                  >
                    <HStack spacing={3} mb={6}>
                      <Circle size="40px" bg="gray.100" color="gray.600">
                        <Icon as={FaChartBar} boxSize={4} />
                      </Circle>
                      <VStack align="start" spacing={0}>
                        <Heading size="md" color={textColor}>
                          Detailed Metrics Comparison
                        </Heading>
                        <Text fontSize="sm" color={subtitleColor}>
                          Complete breakdown of all business metrics
                        </Text>
                      </VStack>
                    </HStack>

                    <Table variant="simple" size="md">
                      <Thead>
                        <Tr>
                          <Th borderColor={borderColor}>Business</Th>
                          <Th borderColor={borderColor} isNumeric>Total Reviews</Th>
                          <Th borderColor={borderColor} isNumeric>Avg Rating</Th>
                          <Th borderColor={borderColor} isNumeric>Positive %</Th>
                          <Th borderColor={borderColor} isNumeric>Reviews/Month</Th>
                          <Th borderColor={borderColor}>Trend</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {compareSelectedBusinessesData.map((business) => {
                          const sentiment = business.data?.sentimentBreakdown || {
                            positive: 0,
                            neutral: 0,
                            negative: 0,
                          };
                          const total = sentiment.positive + sentiment.neutral + sentiment.negative;
                          const positivePercent = total > 0 ? ((sentiment.positive / total) * 100) : 0;
                          const monthly = Object.values(business.data?.monthlyReviewCounts || {});
                          const avgMonthly = monthly.length > 0
                            ? (monthly.reduce((a, b) => a + b, 0) / monthly.length).toFixed(1)
                            : '0';
                          
                          // Calculate trend (last 3 months vs previous 3)
                          let trendUp = null;
                          if (monthly.length >= 6) {
                            const recent = monthly.slice(-3).reduce((a, b) => a + b, 0);
                            const older = monthly.slice(-6, -3).reduce((a, b) => a + b, 0);
                            trendUp = recent > older;
                          }

                          return (
                            <Tr key={business.name}>
                              <Td borderColor={borderColor}>
                                <HStack spacing={3}>
                                  <Circle size="10px" bg={getCompareColor(business.name)} />
                                  <Text fontWeight="medium" noOfLines={1} maxW="200px">
                                    {business.name}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td borderColor={borderColor} isNumeric fontWeight="semibold">
                                {(business.data?.totalReviews || 0).toLocaleString()}
                              </Td>
                              <Td borderColor={borderColor} isNumeric>
                                <HStack justify="flex-end" spacing={1}>
                                  <Icon as={FaStar} color="yellow.400" boxSize={3} />
                                  <Text fontWeight="semibold">
                                    {business.data?.averageRating?.toFixed(1) || 'N/A'}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td borderColor={borderColor} isNumeric>
                                <Badge
                                  colorScheme={positivePercent >= 80 ? 'green' : positivePercent >= 60 ? 'yellow' : 'red'}
                                  borderRadius="full"
                                  px={2}
                                >
                                  {positivePercent.toFixed(0)}%
                                </Badge>
                              </Td>
                              <Td borderColor={borderColor} isNumeric fontWeight="semibold">
                                {avgMonthly}
                              </Td>
                              <Td borderColor={borderColor}>
                                {trendUp !== null && (
                                  <HStack spacing={1}>
                                    <Icon
                                      as={trendUp ? FaArrowUp : FaArrowDown}
                                      color={trendUp ? 'green.500' : 'red.500'}
                                      boxSize={3}
                                    />
                                    <Text
                                      fontSize="sm"
                                      color={trendUp ? 'green.500' : 'red.500'}
                                      fontWeight="medium"
                                    >
                                      {trendUp ? 'Growing' : 'Declining'}
                                    </Text>
                                  </HStack>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* SWOT Analysis Comparison */}
                  {compareSwotData.length > 0 && (
                    <Box
                      bg={cardBg}
                      borderRadius="lg"
                      p={6}
                      shadow="lg"
                      border="1px solid"
                      borderColor={borderColor}
                    >
                      <HStack spacing={3} mb={6}>
                        <Circle size="40px" bg="purple.100" color="purple.600">
                          <Icon as={FaBalanceScale} boxSize={4} />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color={textColor}>
                            SWOT Analysis Comparison
                          </Heading>
                          <Text fontSize="sm" color={subtitleColor}>
                            Compare what each business does well vs. areas for improvement
                          </Text>
                        </VStack>
                      </HStack>

                      <VStack spacing={6} align="stretch">
                        {compareSwotData.map((business) => (
                          <Box
                            key={business.name}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="xl"
                            overflow="hidden"
                          >
                            {/* Business Header */}
                            <Box
                              bg={business.color}
                              px={4}
                              py={3}
                            >
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={business.name}
                                  src={business.maps_uri ? businessImages[business.maps_uri] : null}
                                  bg="white"
                                  color={business.color}
                                />
                                <Text fontWeight="bold" color="white" fontSize="md">
                                  {business.name}
                                </Text>
                              </HStack>
                            </Box>

                            {/* Strengths vs Weaknesses Grid */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                              {/* Strengths Column */}
                              <Box
                                p={5}
                                bg={strengthsBg}
                                borderRightWidth={{ base: 0, md: '1px' }}
                                borderBottomWidth={{ base: '1px', md: 0 }}
                                borderColor={strengthsBorder}
                                minH="200px"
                              >
                                <HStack mb={3} spacing={2}>
                                  <Flex
                                    w="28px"
                                    h="28px"
                                    justify="center"
                                    align="center"
                                    flexShrink={0}
                                    bg="green.100"
                                    borderRadius="md"
                                  >
                                    <Icon as={GiStrongMan} color="green.600" boxSize={4} />
                                  </Flex>
                                  <Text fontWeight="semibold" color={SWOT_COLORS.strengths.heading} fontSize="md">
                                    Strengths
                                  </Text>
                                  <Badge colorScheme="green" fontSize="xs">What they do well</Badge>
                                </HStack>
                                <Box>
                                  {business.strengths ? (
                                    <ReactMarkdown components={swotMarkdownComponents}>
                                      {business.strengths}
                                    </ReactMarkdown>
                                  ) : (
                                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                      No strengths analysis available
                                    </Text>
                                  )}
                                </Box>
                              </Box>

                              {/* Weaknesses Column */}
                              <Box
                                p={5}
                                bg={weaknessesBg}
                                borderColor={weaknessesBorder}
                                minH="200px"
                              >
                                <HStack mb={3} spacing={2}>
                                  <Flex
                                    w="28px"
                                    h="28px"
                                    justify="center"
                                    align="center"
                                    flexShrink={0}
                                    bg="red.100"
                                    borderRadius="md"
                                  >
                                    <Icon as={MdOutlineSick} color="red.600" boxSize={4} />
                                  </Flex>
                                  <Text fontWeight="semibold" color={SWOT_COLORS.weaknesses.heading} fontSize="md">
                                    Weaknesses
                                  </Text>
                                  <Badge colorScheme="red" fontSize="xs">Areas to improve</Badge>
                                </HStack>
                                <Box>
                                  {business.weaknesses ? (
                                    <ReactMarkdown components={swotMarkdownComponents}>
                                      {business.weaknesses}
                                    </ReactMarkdown>
                                  ) : (
                                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                      No weaknesses analysis available
                                    </Text>
                                  )}
                                </Box>
                              </Box>
                            </SimpleGrid>
                          </Box>
                        ))}
                      </VStack>

                      {/* Note about SWOT data availability */}
                      {compareSwotData.length < compareSelectedBusinesses.length && (
                        <Box mt={4} p={3} bg={statBg} borderRadius="md">
                          <Text fontSize="sm" color={subtitleColor}>
                            <Icon as={FaBalanceScale} mr={2} />
                            SWOT analysis data is shown for {compareSwotData.length} of {compareSelectedBusinesses.length} selected businesses. 
                            Other businesses may not have analysis data available yet.
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </VStack>
              )}
            </VStack>
          </Collapse>
        </Box>
      </VStack>

      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default BusinessInsightsDashboard; 